import React from 'react';
import { FormControl, InputV2, TextV2 } from '@withjoy/joykit';
import { StyledContent, StyledFormStack, StyledHeaderStack, StyledInputsStack, StyledTitleStack, styles } from '../UnlockForm/UnlockForm.styles';
import { StyledInputWrapper } from '../shared/UnlockInputWrapper.styles';
import { useContactHostFormController } from './ContactHostForm.controller';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { body4ToSubHeadingOverride, hed6ToSubHeadingOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useDeferredInputFocus } from '../useDeferredInputFocus';
import { useUnlockDialogInputStyles } from '../useUnlockDialogButtonStyles';
import { useTranslation } from '@shared/core';
import { UnlockSubmitButton } from '../shared/UnlockSubmitButton';

export interface ContactHostFormProps {
  eventId: string;
  onClose: () => void;
}

export const ContactHostForm: React.FC<ContactHostFormProps> = ({ eventId, onClose }) => {
  const { formik, loading } = useContactHostFormController({ eventId, onClose });
  const { tq } = useTranslation('guestUnlock');
  const inputRef = useDeferredInputFocus();
  const inputStyles = useUnlockDialogInputStyles();

  return (
    <StyledContent __css={styles.content}>
      <StyledHeaderStack>
        <StyledTitleStack>
          <GuestSiteTypographyOverride override={hed6ToSubHeadingOverride}>
            <TextV2 typographyVariant="hed6">{tq`Ask the host`}</TextV2>
          </GuestSiteTypographyOverride>
          <GuestSiteTypographyOverride override={body4ToSubHeadingOverride}>
            <TextV2 typographyVariant="body4">{tq`Tell the host who you are, so they can help you find your invitation.`}</TextV2>
          </GuestSiteTypographyOverride>
        </StyledTitleStack>
      </StyledHeaderStack>
      <StyledFormStack
        spacing={6}
        __css={styles.formStack}
      >
        <form onSubmit={formik.handleSubmit}>
          <StyledInputsStack>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={tq`Your Full Name`}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  ref={inputRef}
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={tq`e.g. Sarah Smith`}
                  isInvalid={(!!formik.errors.name && formik.touched.name) || undefined}
                  isDisabled={loading}
                  {...formik.getFieldProps('name')}
                />
              </FormControl>
            </StyledInputWrapper>
            <StyledInputWrapper $focusColor={inputStyles.focusColor}>
              <FormControl
                label={tq`Your Email`}
                overrides={{ Label: { props: { color: 'inherit' } } }}
              >
                <InputV2
                  overrides={{ Root: { props: { borderColor: inputStyles.borderColor } } }}
                  placeholder={tq`e.g. sarahsmith@example.com`}
                  isInvalid={(!!formik.errors.email && formik.touched.email) || undefined}
                  isDisabled={loading}
                  type="email"
                  autoComplete="email"
                  {...formik.getFieldProps('email')}
                />
              </FormControl>
            </StyledInputWrapper>
          </StyledInputsStack>
          <UnlockSubmitButton
            width={'100%'}
            shape="rounded"
            variant="outline"
            type="submit"
            loading={loading}
          >
            {tq`Send Request`}
          </UnlockSubmitButton>
        </form>
      </StyledFormStack>
    </StyledContent>
  );
};
